'use client';

import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, RefreshCw } from 'lucide-react';
import {
  useOrganizationMetadata,
  useUpdateOrganizationMetadata,
} from '@/lib/api/account-manager';

interface AccessControlTabProps {
  accountId: string | undefined;
}

// Types for organization metadata (from API response - all fields optional)
interface OrganizationMetadataResponse {
  billing?: {
    unpaidInvoices?: boolean;
  };
  requireAction?: {
    suspended?: boolean;
    manualReview?: boolean;
    blockAccessUntilPaid?: boolean;
    disablePaymentMethodPrompt?: boolean;
    allowUnpaidInvoices?: boolean;
  };
  compliance?: {
    manualReviewRequired?: boolean;
  };
  capabilities?: {
    rawCardTokenization?: boolean;
  };
}

// Local state type with required booleans for form editing
interface LocalMetadataState {
  billing: {
    unpaidInvoices: boolean;
  };
  requireAction: {
    suspended: boolean;
    manualReview: boolean;
    blockAccessUntilPaid: boolean;
    disablePaymentMethodPrompt: boolean;
    allowUnpaidInvoices: boolean;
  };
  capabilities: {
    rawCardTokenization: boolean;
  };
}

// Flag configuration
const flagConfig = {
  billing: {
    title: 'Billing',
    flags: [
      {
        key: 'unpaidInvoices',
        label: 'Unpaid Invoices',
        description: 'Account has unpaid invoices pending',
      },
    ],
  },
  requireAction: {
    title: 'Require Action',
    flags: [
      {
        key: 'suspended',
        label: 'Suspended',
        description: 'Account is suspended (blocks all access)',
        critical: true,
      },
      {
        key: 'manualReview',
        label: 'Manual Review',
        description: 'Account requires manual review before processing',
        critical: true,
      },
      {
        key: 'blockAccessUntilPaid',
        label: 'Block Access Until Paid',
        description: 'Block access until outstanding balance is paid',
        critical: true,
      },
      {
        key: 'disablePaymentMethodPrompt',
        label: 'Disable Payment Method Prompt',
        description: "Don't prompt user to update payment method",
      },
      {
        key: 'allowUnpaidInvoices',
        label: 'Allow Unpaid Invoices',
        description: 'Allow account to continue operating with unpaid invoices',
      },
    ],
  },
  capabilities: {
    title: 'Capabilities',
    flags: [
      {
        key: 'rawCardTokenization',
        label: 'Raw card data (server-side tokenization)',
        description:
          'Lets the merchant tokenize cards from its own server with node-sdk createFromCard(). Only for PCI-compliant merchants that already handle card numbers.',
      },
    ],
  },
} as const;

// Default metadata structure
const defaultMetadata: LocalMetadataState = {
  billing: { unpaidInvoices: false },
  requireAction: {
    suspended: false,
    manualReview: false,
    blockAccessUntilPaid: false,
    disablePaymentMethodPrompt: false,
    allowUnpaidInvoices: false,
  },
  capabilities: { rawCardTokenization: false },
};

export function AccessControlTab({ accountId }: AccessControlTabProps) {
  // Fetch organization metadata
  const {
    data: metadataResponse,
    isLoading,
  } = useOrganizationMetadata(accountId);

  const updateMetadataMutation = useUpdateOrganizationMetadata();

  // Local state for editing
  const [localMetadata, setLocalMetadata] = useState<LocalMetadataState>(defaultMetadata);

  // Sync local state when data is fetched
  useEffect(() => {
    if (metadataResponse?.privateMetadata) {
      const pm = metadataResponse.privateMetadata as OrganizationMetadataResponse;
      setLocalMetadata({
        billing: {
          unpaidInvoices: pm.billing?.unpaidInvoices ?? false,
        },
        requireAction: {
          suspended: pm.requireAction?.suspended ?? false,
          manualReview:
            pm.compliance?.manualReviewRequired ?? pm.requireAction?.manualReview ?? false,
          blockAccessUntilPaid: pm.requireAction?.blockAccessUntilPaid ?? false,
          disablePaymentMethodPrompt: pm.requireAction?.disablePaymentMethodPrompt ?? false,
          allowUnpaidInvoices: pm.requireAction?.allowUnpaidInvoices ?? false,
        },
        capabilities: { rawCardTokenization: pm.capabilities?.rawCardTokenization ?? false },
      });
    }
  }, [metadataResponse]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!metadataResponse?.privateMetadata) return false;
    const pm = metadataResponse.privateMetadata as OrganizationMetadataResponse;

    const serverMetadata: LocalMetadataState = {
      billing: { unpaidInvoices: pm.billing?.unpaidInvoices ?? false },
      requireAction: {
        suspended: pm.requireAction?.suspended ?? false,
        manualReview:
          pm.compliance?.manualReviewRequired ?? pm.requireAction?.manualReview ?? false,
        blockAccessUntilPaid: pm.requireAction?.blockAccessUntilPaid ?? false,
        disablePaymentMethodPrompt: pm.requireAction?.disablePaymentMethodPrompt ?? false,
        allowUnpaidInvoices: pm.requireAction?.allowUnpaidInvoices ?? false,
      },
      capabilities: { rawCardTokenization: pm.capabilities?.rawCardTokenization ?? false },
    };

    return JSON.stringify(localMetadata) !== JSON.stringify(serverMetadata);
  }, [localMetadata, metadataResponse]);

  // Handle toggle change
  const handleToggle = (
    section: 'billing' | 'requireAction' | 'capabilities',
    key: string,
    value: boolean
  ) => {
    setLocalMetadata((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  // Handle save
  const handleSave = () => {
    if (!accountId) return;

    updateMetadataMutation.mutate({
      accountId,
      privateMetadata: {
        billing: localMetadata.billing,
        requireAction: localMetadata.requireAction,
        compliance: {
          manualReviewRequired: localMetadata.requireAction.manualReview,
        },
        capabilities: localMetadata.capabilities,
      },
    });
  };

  // Handle reset
  const handleReset = () => {
    if (metadataResponse?.privateMetadata) {
      const pm = metadataResponse.privateMetadata as OrganizationMetadataResponse;
      setLocalMetadata({
        billing: { unpaidInvoices: pm.billing?.unpaidInvoices ?? false },
        requireAction: {
          suspended: pm.requireAction?.suspended ?? false,
          manualReview:
            pm.compliance?.manualReviewRequired ?? pm.requireAction?.manualReview ?? false,
          blockAccessUntilPaid: pm.requireAction?.blockAccessUntilPaid ?? false,
          disablePaymentMethodPrompt: pm.requireAction?.disablePaymentMethodPrompt ?? false,
          allowUnpaidInvoices: pm.requireAction?.allowUnpaidInvoices ?? false,
        },
        capabilities: { rawCardTokenization: pm.capabilities?.rawCardTokenization ?? false },
      });
    }
  };

  if (!accountId) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">Loading account information...</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Access Control
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Access Control
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={!hasChanges || updateMetadataMutation.isPending}
          >
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || updateMetadataMutation.isPending}
          >
            {updateMetadataMutation.isPending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Billing Flags */}
        <div>
          <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
            {flagConfig.billing.title}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {flagConfig.billing.flags.map((flag) => (
              <FlagItem
                key={flag.key}
                label={flag.label}
                description={flag.description}
                value={localMetadata.billing?.[flag.key as keyof typeof localMetadata.billing] ?? false}
                onChange={(value) => handleToggle('billing', flag.key, value)}
                disabled={updateMetadataMutation.isPending}
              />
            ))}
          </div>
        </div>

        {/* Require Action Flags */}
        <div>
          <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center gap-2">
            {flagConfig.requireAction.title}
            <Badge variant="outline" className="text-xs font-normal">
              Critical
            </Badge>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {flagConfig.requireAction.flags.map((flag) => (
              <FlagItem
                key={flag.key}
                label={flag.label}
                description={flag.description}
                value={localMetadata.requireAction?.[flag.key as keyof typeof localMetadata.requireAction] ?? false}
                onChange={(value) => handleToggle('requireAction', flag.key, value)}
                disabled={updateMetadataMutation.isPending}
                critical={'critical' in flag && flag.critical}
              />
            ))}
          </div>
        </div>

        {/* Capabilities (opt-in platform features) */}
        <div>
          <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center gap-2">
            {flagConfig.capabilities.title}
            <Badge variant="outline" className="text-xs font-normal">
              Opt-in
            </Badge>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {flagConfig.capabilities.flags.map((flag) => (
              <FlagItem
                key={flag.key}
                label={flag.label}
                description={flag.description}
                value={localMetadata.capabilities?.[flag.key as keyof typeof localMetadata.capabilities] ?? false}
                onChange={(value) => handleToggle('capabilities', flag.key, value)}
                disabled={updateMetadataMutation.isPending}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Individual flag item component
interface FlagItemProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  critical?: boolean;
}

function FlagItem({ label, description, value, onChange, disabled, critical }: FlagItemProps) {
  return (
    <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${value ? (critical ? 'bg-red-500' : 'bg-green-500') : 'bg-gray-300'}`} />
          <span className="font-medium text-sm">{label}</span>
          {critical && value && (
            <Badge variant="destructive" className="text-xs">
              Active
            </Badge>
          )}
        </div>
        <Switch
          checked={value}
          onCheckedChange={onChange}
          disabled={disabled}
        />
      </div>
      <p className="text-xs text-muted-foreground pl-5">{description}</p>
    </div>
  );
}
